import { CALL_API } from '../../middleware/api';
import uniqueId from '../../utils/uniqueId';
import { loadUsers, saveUsers } from './user';
import { loadHarvcoTags, saveHarvcoTags } from './harvcoTag';
import { loadIndustries, saveIndustries } from './industry';
import { loadUniverse } from './universe';

export const SHOW_PROJECT_LOADING = Symbol('SHOW_PROJECT_LOADING');
export const REQUEST_API_PROJECT_FAILURE = Symbol('REQUEST_API_PROJECT_FAILURE');
export const SAVED_PROJECT_SUCCESS = Symbol('SAVED_PROJECT_SUCCESS');
export const LOADED_PROJECT_SUCCESS = Symbol('LOADED_PROJECT_SUCCESS');
export const UPDATE_PROJECT_INFORMATION = Symbol('UPDATE_PROJECT_INFORMATION');
export const RESET_ALL_ROW_TO_VIEW_MODE = Symbol('RESET_ALL_ROW_TO_VIEW_MODE');
export const RESET_ERROR_LIST = Symbol('RESET_ERROR_LIST');
export const SAVED_HARVCO_TAG_SUCCESS = Symbol('SAVED_HARVCO_TAG_SUCCESS');
export const REVERT_PROJECT_CHANGES = Symbol('REVERT_PROJECT_CHANGES');
export const UPDATE_PROJECT_COMMON_INFO = Symbol('UPDATE_PROJECT_COMMON_INFO');

export const SELECT_PROJECT_SUGGESTION = Symbol('SELECT_PROJECT_SUGGESTION');
export const LOADING_PROJECT_SUGGESTION = Symbol('LOADING_PROJECT_SUGGESTION');
export const LOADED_PROJECT_SUGGESTION_FAILURE = Symbol('LOADED_PROJECT_SUGGESTION_FAILURE');
export const LOADED_PROJECT_SUGGESTION_SUCCESS = Symbol('LOADED_PROJECT_SUGGESTION_SUCCESS');

export const LOADING_PROJECT_TYPES = Symbol('LOADING_PROJECT_TYPES');
export const LOADING_PROJECT_TYPES_SUCCESS = Symbol('LOADING_PROJECT_TYPES_SUCCESS');

/**
 * Save project general information.
 */
export function saveProject() {
  return (dispatch, getState) => {
    const state = getState().eproject.projectData;
    const projectId = state.get('id');
    const body = {
      category: state.getIn(['category', 'name']),
      details: state.getIn(['details', 'name']),
      projectType: state.getIn(['projectType', 'name']) === '' ? null : state.getIn(['projectType', 'name']),
      status: state.getIn(['status', 'value']),
      harvcoTags: state.get('harvcoTags'),
    };
    const harvcoLeadId = state.getIn(['harvcoLead', 'id']);

    if (harvcoLeadId === -1) {
      body.harvco_lead_id = null;
    }

    if (harvcoLeadId > 0) {
      body.harvco_lead_id = harvcoLeadId;
    }

    const clientLeadId = state.getIn(['clientLead', 'id']);

    if (clientLeadId === -1) {
      body.client_lead_id = null;
    }

    if (clientLeadId > 0) {
      body.client_lead_id = clientLeadId;
    }

    return dispatch({
      fields: Object.keys(body),
      [CALL_API]: {
        method: 'put',
        path: `/api/v1/ceo_projects/${projectId}`,
        body,
        startType: SHOW_PROJECT_LOADING,
        errorType: REQUEST_API_PROJECT_FAILURE,
        successType: SAVED_PROJECT_SUCCESS,
      },
    });
  };
}

/**
 * Load page's data initially.
 *
 * @param id {Number} Project's id.
 * @param afterSuccess {Function}.
 * @param isReload {Boolean} If false concat data, for lazy loading.
 * @param all {Number} Load all or active projects (available 0 || 1).
 * @returns Action.
 */
export function loadPage(id, { afterSuccess } = {}, { isReload, all } = { isReload: false, all: 0 }) {
  return {
    [CALL_API]: {
      method: 'get',
      path: `/api/v1/ceo_projects/${id}`,
      afterSuccess: ({ dispatch }) => {
        dispatch(clearError());
        dispatch(loadProjectTypes());
        dispatch(loadProject(id));
        dispatch(loadHarvcoTags(id));
        dispatch(loadUsers(id));
        dispatch(loadIndustries(id));
        dispatch(loadUniverse(id, { limit: 60, all }, { afterSuccess }, isReload));
      },
    },
  };
}

/**
 * Save all page's information.
 *
 * @returns Action.
 */
export function savePage() {
  return dispatch => {
    dispatch(clearError());
    dispatch(saveUsers());
    dispatch(saveProject());
    dispatch(saveHarvcoTags());
  };
}

/**
 * Save information edited in the project details popup: users, general information, harvco tags and industries.
 *
 * @returns Action. Resolves to true when every request succeeded.
 */
export function saveProjectDetails() {
  return (dispatch, getState) => {
    dispatch(clearError());

    return Promise.all([
      dispatch(saveUsers()),
      dispatch(saveProject()),
      dispatch(saveHarvcoTags()),
      dispatch(saveIndustries()),
    ]).then(() => getState().eproject.common.get('errors').size === 0);
  };
}

/**
 * Load project information.
 *
 * @param id {Number} Project's id.
 */
export function loadProject(id) {
  return {
    [CALL_API]: {
      method: 'get',
      path: `/api/v1/ceo_projects/${id}`,
      query: { include: 'client_lead,harvco_lead,buyer' },
      startType: SHOW_PROJECT_LOADING,
      errorType: REQUEST_API_PROJECT_FAILURE,
      successType: LOADED_PROJECT_SUCCESS,
    },
  };
}

function getEndPoint(field, value, buyerId) {
  if (field === 'harvcoLead') {
    return {
      path: '/api/v1/browse/users_by_role',
      query: { like: value, role: 'director' },
    };
  }

  return {
    path: `/api/v1/browse/buyers/${buyerId}/people`,
    query: { like: value },
  };
}

/**
 * Load project types.
 */
const loadProjectTypes = () => ({
  [CALL_API]: {
    method: 'get',
    path: '/api/v1/browse/ceo_project_types',
    startType: LOADING_PROJECT_TYPES,
    successType: LOADING_PROJECT_TYPES_SUCCESS,
  },
});

/**
 * Update project and suggest information based on user's input.
 *
 * @param field {String} Name of project's field.
 * @param value {*} Value to input.
 * @returns Action.
 */
export function updateProjectInfo(field, value = '') {
  const sugs = ['harvcoLead', 'clientLead', 'projectType'];

  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_PROJECT_INFORMATION,
      field,
      value,
    });
    if (sugs.indexOf(field) > -1) {
      const buyerId = getState().eproject.projectData.get('buyerId');

      dispatch({
        [CALL_API]: {
          ...getEndPoint(field, value, buyerId),
          method: 'get',
          startType: LOADING_PROJECT_SUGGESTION,
          errorType: LOADED_PROJECT_SUGGESTION_FAILURE,
          successType: LOADED_PROJECT_SUGGESTION_SUCCESS,
        },
        requestId: uniqueId(),
        field,
      });
    }
  };
}

/**
 * Switch all rows to view mode.
 *
 * @returns Action.
 */
export function reset() {
  return {
    type: RESET_ALL_ROW_TO_VIEW_MODE,
  };
}

/**
 * Select a suggestion.
 *
 * @param field {exec|harvcoLead|clientLead} Project field.
 * @param suggestionId {Number} Suggestion id.
 * @param suggestionName {String} Suggestion name.
 * @returns Action.
 */
export function selectProjectSuggestion(field, suggestionId, suggestionName) {
  return {
    type: SELECT_PROJECT_SUGGESTION,
    field,
    suggestionId,
    suggestionName,
  };
}

/**
 * Reset error list.
 *
 * @returns Action.
 */
export function clearError() {
  return {
    type: RESET_ERROR_LIST,
  };
}

/**
 * Revert changes which didn't save.
 */
export function revertProjectChanges() {
  return {
    type: REVERT_PROJECT_CHANGES,
  };
}
